<template>
  <d-container
    fluid
    :class="{
      'main-content-container': true,
      'px-4': !isOrderView,
      'px-0': isOrderView
    }">
    <!-- Page Header -->
    <d-row
      no-gutters
      :class="{
        'page-header': true,
        'py-4': !isOrderView,
        'mb-4 pb-3 border-bottom': isOrderView
      }">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span
          v-if="!isOrderView"
          class="text-uppercase page-subtitle">
          {{ $t('notifications.notificationsLabel') }}
        </span>
        <h3
          v-if="isDashboardList"
          class="page-title">{{ $t('notifications.notificationsNearest') }}</h3>
        <h3
          v-else-if="isOrderView"
          class="page-title">{{ $t('notifications.notificationsLabel') }}</h3>
        <h3
          v-else
          class="page-title">{{ $t('notifications.notifications') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="isDashboardList"
          theme="secondary"
          @click.prevent="$router.push('/powiadomienia')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('notifications.goToFullList') }}
        </d-button>

        <d-button
          theme="success"
          @click.prevent="isOrderView ? $bus.$emit('show-order-notification-new-popup') : $router.push('/powiadomienia/nowe')">
          <i class="material-icons">add</i>
          {{ $t('notifications.addNewNotification') }}
        </d-button>
      </d-col>
    </d-row>

    <template v-if="!isDashboardList && !isOrderView">
      <d-row class="d-flex ml-0 mb-3 page-filters">
        <d-col cols="12" class="d-flex pl-0">
          <d-input
            :placeholder="$t('ui.search')"
            v-model="filters.search"
            key="filters-search"
            class="mr-2 ml-0 w-25" />

          <d-input
            :placeholder="$t('purchase.enterOrderName')"
            v-model="filters.order_name"
            key="filters-order-name"
            class="w-25 mr-2" />

          <date-range-picker
            ref="picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="w-50"
            style="min-width: 200px;"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="dateRange"
            :ranges="false"
            :linkedCalendars="true">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
            </template>
          </date-range-picker>
        </d-col>
      </d-row>

      <d-row
        style="width: 100%;"
        class="d-flex ml-0 mb-3 page-filters">
        <d-col cols="12" class="d-flex pl-0 pr-0">
          <select-customer
            :customerID="filters.customer_id"
            class="w-50 mr-2" />

          <select-customer
            :customerID="filters.potential_customer_id"
            customerType="potential"
            class="w-50 mr-2" />

          <select-customer
            :customerID="filters.market_customer_id"
            customerType="market"
            class="w-50" />
        </d-col>
      </d-row>

      <d-row
        style="width: 100%;"
        class="d-flex ml-0 mb-3 page-filters">
        <d-button-group class="mr-5">
          <d-button
            :outline="filters.is_completed !== -1"
            @click.prevent="setFilterIsCompleted(-1)">
            {{ $t('notifications.completedFilterAll') }}
          </d-button>
          <d-button
            :outline="filters.is_completed !== 0"
            @click.prevent="setFilterIsCompleted(0)">
            {{ $t('notifications.completedFilterIsNotCompleted') }}
          </d-button>
          <d-button
            :outline="filters.is_completed !== 1"
            @click.prevent="setFilterIsCompleted(1)">
            {{ $t('notifications.completedFilterIsCompleted') }}
          </d-button>
        </d-button-group>

        <d-checkbox
          inline
          v-model="filters.show_in_dashboard"
          toggle
          toggle-small
          class="mt-1">
          {{ $t('notifications.showInDashboardFilter') }}
        </d-checkbox>

        <d-checkbox
          inline
          v-model="filters.only_future"
          toggle
          toggle-small
          class="mt-1 ml-2">
          {{ $t('notifications.onlyFutureFilter') }}
        </d-checkbox>

        <d-checkbox
          v-if="userIsAdmin"
          inline
          v-model="filters.show_all"
          toggle
          toggle-small
          class="mt-1 ml-2">
          {{ $t('notifications.showAllFilter') }}
        </d-checkbox>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-auto mr-2"
          @click.prevent="submitFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap"
          @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
      </d-row>
    </template>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('notifications.description') }}</th>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('notifications.dateTime') }}</th>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('notifications.form.isRepeatable') }}</th>
            <th scope="col" class="border-0" style="width: 80px;">{{ $t('notifications.showInDashboard') }}</th>
            <th scope="col" class="border-0" style="width: 80px;">{{ $t('notifications.sendReminder') }}</th>
            <th scope="col" class="border-0" style="width: 130px;">{{ $t('notifications.reminderBefore') }}</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <template v-for="(notification, index) of notifications">
            <tr :key="'notification-' + index">
              <td class="align-middle">
                {{ notification.description }}
                <template v-if="userIsAdmin && notification.user">
                  <br>
                  <small class="pt-2 d-block">
                    {{ $t('users.user') }}:
                    <strong>{{ notification.user.name }}</strong>
                  </small>
                  <template v-if="notification.order">
                    <small>
                      {{ $t('notifications.order') }}:
                      <strong>{{ notification.order.order_name }}</strong>
                    </small>
                  </template>
                </template>

                <a
                  v-if="notification.children.length"
                  href="#"
                  @click.prevent="toggleChildrenNotifications(index)">
                  <small v-if="showChildrenNotifications[index]">
                    {{ $t('notifications.hideChildrenNotifications') }} ({{ notification.children.length }})
                  </small>
                  <small v-else>
                    {{ $t('notifications.showChildrenNotifications') }} ({{ notification.children.length }})
                  </small>
                </a>
              </td>

              <td class="align-middle">
                {{ notification.date }}<br>
                {{ notification.time }}
              </td>

              <td class="align-middle">
                <span
                  v-if="notification.is_repeatable"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </span>
                <span
                  v-else
                  class="text-danger">
                  {{ $t('ui.no') }}
                </span>

                <small
                  v-if="notification.is_repeatable"
                  class="d-block">
                  {{ $t('notifications.form.frequencyValues.' + (notification.frequency).toString().toLowerCase()) }}<br>
                  <strong v-if="notification.repeat_number === 0">
                    &infin;
                  </strong>
                  <strong v-else>
                    {{ notification.repeat_number }}
                  </strong> {{ $t('notifications.times') }}
                </small>
              </td>

              <td class="align-middle">
                <span
                  v-if="notification.show_in_dashboard"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </span>
                <span
                  v-if="!notification.show_in_dashboard"
                  class="text-danger">
                  {{ $t('ui.no') }}
                </span>
              </td>

              <td class="align-middle">
                <span
                  v-if="notification.send_reminder"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </span>
                <span
                  v-if="!notification.send_reminder"
                  class="text-danger">
                  {{ $t('ui.no') }}
                </span>
              </td>

              <td class="align-middle">
                {{ parseInt(notification.reminder_before, 10) }} h
              </td>

              <td
                class="align-middle"
                style="width: 150px;">
                <d-button
                  v-if="!notification.is_completed"
                  theme="success"
                  :outline="true"
                  @click.native="markAsCompleted(notification.id)"
                  :disabled="markingInProgress !== false"
                  :title="$t('notifications.markAsCompleted')">
                  <div
                    v-if="markingInProgress === notification.id"
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                  <i
                    v-else
                    class="material-icons">check</i>
                </d-button>
                <d-button
                  v-if="notification.is_completed"
                  theme="success"
                  @click.native="markAsNotCompleted(notification.id)"
                  :disabled="markingInProgress !== false"
                  :title="$t('notifications.markAsNotCompleted')">
                  <div
                    v-if="markingInProgress === notification.id"
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                  <i
                    v-else
                    class="material-icons">check</i>
                </d-button>
                <d-button
                  theme="primary"
                  @click.native="$router.push('/powiadomienia/edycja/' + notification.id)"
                  class="ml-2 mr-2"
                  :title="$t('ui.edit')">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  @click.native="removeNotification(notification.id)"
                  :disabled="!!removeInProgress"
                  :title="$t('ui.delete')">
                  <i class="material-icons">delete</i>
                </d-button>
              </td>
            </tr>

            <template v-for="(subnotification, subindex) of notification.children">
              <tr
                v-if="showChildrenNotifications[index]"
                :key="'notification-child-' + index + '-' + subindex"
                class="sub-notification">
                <td class="align-middle pl-4">
                  {{ subnotification.description }}
                  <template v-if="userIsAdmin && subnotification.user">
                    <br>
                    <small class="pt-2 d-block">
                      {{ $t('users.user') }}:
                      <strong>{{ subnotification.user.name }}</strong>
                    </small>
                    <template v-if="subnotification.order">
                      <small>
                        {{ $t('notifications.order') }}:
                        <strong>{{ subnotification.order.order_name }}</strong>
                      </small>
                    </template>
                  </template>
                </td>

                <td class="align-middle">
                  {{ subnotification.date }}<br>
                  {{ subnotification.time }}
                </td>

                <td class="align-middle">
                  <span
                    v-if="subnotification.is_repeatable"
                    class="text-success">
                    {{ $t('ui.yes') }}
                  </span>
                  <span
                    v-else
                    class="text-danger">
                    {{ $t('ui.no') }}
                  </span>

                  <small
                    v-if="subnotification.is_repeatable"
                    class="d-block">
                    {{ $t('notifications.form.frequencyValues.' + (subnotification.frequency).toString().toLowerCase()) }}<br>
                    <strong v-if="subnotification.repeat_number === 0">
                      &infin;
                    </strong>
                    <strong v-else>
                      {{ subnotification.repeat_number }}
                    </strong> {{ $t('notifications.times') }}
                  </small>
                </td>

                <td class="align-middle">
                  <span
                    v-if="subnotification.show_in_dashboard"
                    class="text-success">
                    {{ $t('ui.yes') }}
                  </span>
                  <span
                    v-if="!subnotification.show_in_dashboard"
                    class="text-danger">
                    {{ $t('ui.no') }}
                  </span>
                </td>

                <td class="align-middle">
                  <span
                    v-if="subnotification.send_reminder"
                    class="text-success">
                    {{ $t('ui.yes') }}
                  </span>
                  <span
                    v-if="!subnotification.send_reminder"
                    class="text-danger">
                    {{ $t('ui.no') }}
                  </span>
                </td>

                <td class="align-middle">
                  {{ parseInt(subnotification.reminder_before, 10) }} h
                </td>

                <td
                  class="align-middle"
                  style="width: 150px;">
                  <d-button
                    v-if="!subnotification.is_completed"
                    theme="success"
                    :outline="true"
                    @click.native="markAsCompleted(subnotification.id)"
                    :disabled="markingInProgress !== false"
                    :title="$t('notifications.markAsCompleted')">
                    <div
                      v-if="markingInProgress === subnotification.id"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    <i
                      v-else
                      class="material-icons">check</i>
                  </d-button>
                  <d-button
                    v-if="subnotification.is_completed"
                    theme="success"
                    @click.native="markAsNotCompleted(subnotification.id)"
                    :disabled="markingInProgress !== false"
                    :title="$t('notifications.markAsNotCompleted')">
                    <div
                      v-if="markingInProgress === subnotification.id"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    <i
                      v-else
                      class="material-icons">check</i>
                  </d-button>
                  <d-button
                    theme="primary"
                    @click.native="$router.push('/powiadomienia/edycja/' + subnotification.id)"
                    class="ml-2 mr-2"
                    :title="$t('ui.edit')">
                    <i class="material-icons">edit</i>
                  </d-button>
                  <d-button
                    theme="danger"
                    @click.native="removeNotification(subnotification.id)"
                    :disabled="!!removeInProgress"
                    :title="$t('ui.delete')">
                    <i class="material-icons">delete</i>
                  </d-button>
                </td>
              </tr>
            </template>
          </template>
          <tr v-if="notifications.length === 0">
            <td
              colspan="7"
              class="text-center">
              {{ $t('notifications.noNotificationsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import ListUtils from './../utils/ListUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import Pagination from './../components/common/Pagination.vue';
import SelectCustomer from './../components/common/SelectCustomer.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import Vue from 'vue';

export default {
  name: 'notifications',
  props: {
    isOrderView: {
      default: false,
      type: Boolean
    }
  },
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination,
    'select-customer': SelectCustomer
  },
  computed: {
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    isDashboardList () {
      return !!this.$route.meta.showDashboardResults;
    },
    disableFiltersInSubview () {
      return this.isOrderView;
    }
  },
  data() {
    return {
      notifications: [],
      loaded: false,
      loadError: false,
      markingInProgress: false,
      removeInProgress: false,
      skipLSPagination: false,
      dateRange: {
        startDate: null,
        endDate: null
      },
      filters: {
        order_name: '',
        search: '',
        is_completed: -1,
        only_future: false,
        show_all: this.$store.getters.getUserType === 'admin',
        show_in_dashboard: false,
        customer_id: null,
        market_customer_id: null,
        potential_customer_id: null
      },
      showChildrenNotifications: {}
    };
  },
  created () {
    if (this.isOrderView) {
      this.skipLSPagination = true;
    }
  },
  mounted () {
    this.loadFilteredData();
    this.$bus.$on('notifications-list-reload-needed', this.debouncedLoadFilteredData);
    this.$bus.$on('ui-select-customer-updated', this.setCustomerID);
    this.$bus.$on('ui-select-market-customer-updated', this.setMarketCustomerID);
    this.$bus.$on('ui-select-potential-customer-updated', this.setPotentialCustomerID);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loaded = false;
      this.loadError = false;
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let whereData = {};

      if (this.isDashboardList) {
        whereData = {
          is_near: 1
        };
      } else if (this.isOrderView) {
        whereData = {
          order_id: parseInt(this.$route.params.id, 10)
        };
      } else {
        if (this.filters.is_completed !== -1) {
          whereData.is_completed = this.filters.is_completed;
        }

        if (this.filters.only_future) {
          whereData.only_future = +this.filters.only_future;
        }

        if (this.filters.show_in_dashboard) {
          whereData.show_in_dashboard = +this.filters.show_in_dashboard;
        }

        if (this.userIsAdmin && this.filters.show_all) {
          whereData.show_all = +this.filters.show_all;
        }

        if (this.filters.customer_id) {
          whereData.customer_id = this.filters.customer_id;
        }

        if (this.filters.market_customer_id) {
          whereData.market_customer_id = this.filters.market_customer_id;
        }

        if (this.filters.potential_customer_id) {
          whereData.potential_customer_id = this.filters.potential_customer_id;
        }

        if (this.dateRange.startDate) {
          whereData['date_from'] = this.prepareDateString(this.dateRange.startDate);
        }

        if (this.dateRange.endDate) {
          whereData['date_to'] = this.prepareDateString(this.dateRange.endDate);
        }

        if (this.filters.order_name && this.filters.order_name.trim() !== '') {
          whereData['order_name'] = this.filters.order_name;
        }

        if (this.filters.search && this.filters.search.trim() !== '') {
          whereData['search'] = this.filters.search;
        }
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/notifications/items?page=' + this.currentPage,
        listField: 'notifications',
        loadError: 'loadError',
        params: {
          where: whereData
        },
        successAction () {
          Vue.set(this, 'showChildrenNotifications', {});
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        order_name: '',
        is_completed: -1,
        only_future: false,
        show_all: false,
        show_in_dashboard: false,
        customer_id: null,
        market_customer_id: null,
        potential_customer_id: null
      };

      this.dateRange.startDate = null;
      this.dateRange.endDate = null;

      this.$bus.$emit('ui-select-customer-reset');
      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
      Vue.set(this, 'showChildrenNotifications', {});
    },
    removeNotification (notificationID) {
      this.removeInProgress = notificationID;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/notifications/delete/',
        id: notificationID,
        successTitle: this.$t('notifications.removeNotificationSuccessTitle'),
        successText: this.$t('notifications.removeNotificationSuccessText'),
        confirmTitle: this.$t('notifications.removeNotificationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.removeNotificationErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    markAsCompleted (notificationID) {
      this.markingInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/notifications/mark-completed/' + notificationID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.markingFailedTryAgain'),
        formData: {
          is_completed: 1
        },
        successAction: () => {
          this.markingInProgress = false;
          this.loadFilteredData();
        },
        failAction: () => {
          this.markingInProgress = false;
        }
      });
    },
    markAsNotCompleted (notificationID) {
      this.markingInProgress = notificationID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/notifications/mark-completed/' + notificationID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.markingFailedTryAgain'),
        formData: {
          is_completed: 0
        },
        successAction: () => {
          this.markingInProgress = false;
          this.loadFilteredData();
        },
        failAction: () => {
          this.markingInProgress = false;
        }
      });
    },
    setFilterIsCompleted (value) {
      this.filters.is_completed = value;
    },
    setCustomerID (customerID) {
      this.filters.customer_id = customerID;
    },
    setMarketCustomerID (customerID) {
      this.filters.market_customer_id = customerID;
    },
    setPotentialCustomerID (customerID) {
      this.filters.potential_customer_id = customerID;
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    submitFiltersForm () {
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    toggleChildrenNotifications (index) {
      if (this.showChildrenNotifications[index]) {
        Vue.set(this.showChildrenNotifications, index, false);
      } else {
        Vue.set(this.showChildrenNotifications, index, true);
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('notifications-list-reload-needed', this.debouncedLoadFilteredData);
    this.$bus.$off('ui-select-customer-updated', this.setCustomerID);
    this.$bus.$off('ui-select-market-customer-updated', this.setMarketCustomerID);
    this.$bus.$off('ui-select-potential-customer-updated', this.setPotentialCustomerID);
  }
};
</script>

<style lang="scss" scoped>
.sub-notification {
  background-color: #f5f5f5;
}
</style>
